/**
 * Companion helpers with no selector dependencies, kept apart from {@link ./companion-utils} so
 * selectors can read them without the import cycle its call-selector imports would create.
 */

const isLocalhost = () => {
    try {
        return window.location.hostname.includes('localhost');
    } catch {
        return false;
    }
};

const isLocalDevOverrideEnabled = () => {
    try {
        if (!isLocalhost()) {
            return false;
        }
        return localStorage.getItem('companionMode') === 'true';
    } catch {
        return false;
    }
};

/**
 * The gate for every companion code path. When this is false the client must behave exactly as it
 * did before the companion work existed - no answer delegation, no outbound delegation, no in-call
 * control relay, and the "call on another device" banner back as it was.
 *
 * The switch is owned by the backend (`companion.enabled` plus customer/account lists in the
 * configurator, fetched via `GET /companion/config`), not by the client. That matters because the
 * companion code ships in the same bundle as the ordinary Teams and CTI integrations: the only
 * thing keeping it dormant for everyone else is this flag, so it has to be centrally controllable
 * and revocable without a release. Home enforces it independently on `/companion/*`, so a client
 * that ignored this would still be refused.
 *
 * The localStorage override exists for local development only and is deliberately restricted to
 * localhost, so it can never loosen the switch in production:
 *   localStorage.setItem('companionMode', 'true')
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const isCompanionModeEnabled = (state) => {
    if (state?.app?.get('companionEnabled') === true) {
        return true;
    }
    return isLocalDevOverrideEnabled();
};

export const CompanionLocationStatus = Object.freeze({
    OK: 'ok',
    DENIED: 'denied',
    UNAVAILABLE: 'unavailable',
    OUTSIDE_REGION: 'outside_region',
});

export const STALE_COMPANION_DEVICE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Whether a device from the companion list is too old to relay to. Accepts both shapes the list
 * comes in: the plain DTO as it arrives from Home, and the Immutable Map the reducer stores.
 *
 * A device with no `lastSeen` at all is treated as fresh rather than stale - it means a payload
 * older than the field, and refusing to relay on the strength of a missing field would break
 * working setups for the sake of a guess.
 *
 * @param {object|import('immutable').Map} device
 * @returns {boolean}
 */
export const isStaleCompanionDevice = (device) => {
    if (!device) {
        return false;
    }
    const lastSeen =
        typeof device.get === 'function'
            ? device.get('lastSeen')
            : device.lastSeen;
    if (!lastSeen) {
        return false;
    }
    const timestamp = new Date(lastSeen).getTime();
    return (
        !Number.isNaN(timestamp) &&
        Date.now() - timestamp > STALE_COMPANION_DEVICE_MS
    );
};

export { isLocalhost };
